import {useRoute} from '@typed-router';
import {defineStore, storeToRefs} from 'pinia';
import {computed, ref, useLogger, useTranslation} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {ShortlistProfileResponse, ShortlistResponse} from '@api';
import {Configuration, ShortlistApi} from '@api';
import {useProfileStore} from '../profileStore';

const apiConfiguration = new Configuration({basePath: ''});
const shortlistApi = new ShortlistApi(apiConfiguration);

export const useShortlistStore = defineStore('shortlist', () => {
    const route = useRoute();
    const profileStore = useProfileStore();
    const {t} = useTranslation();
    const logger = useLogger();

    const {profile} = storeToRefs(profileStore);

    const shortlist = ref<ShortlistResponse | null>(null);

    const findItemByAccountId = computed((): ShortlistProfileResponse | undefined =>
        shortlist.value?.profiles.find((shortlistedProfile: ShortlistProfileResponse) => shortlistedProfile.accountId === profile.value!.accountId),
    );

    const isContained = computed((): boolean => {
        if (!shortlist.value?.profiles) {
            return false;
        }
        return shortlist.value.profiles.some(
            (shortlistedProfile: ShortlistProfileResponse) => shortlistedProfile.accountId === profile.value?.accountId,
        );
    });

    const comesFromSearch = computed((): boolean => route?.query?.searchid !== undefined);
    const hasShortlistItems = computed((): boolean => (shortlist.value?.profiles && shortlist.value.profiles.length > 0) || false);
    const profileInShortlist = computed((): boolean => hasShortlistItems.value && isContained.value);

    const accountIds = computed(() =>
        shortlist.value && shortlist.value.profiles.length > 0
            ? shortlist.value.profiles
                  .map((shortlistedProfile: ShortlistProfileResponse) => shortlistedProfile.accountId)
                  .concat(profile.value!.accountId)
            : [profile.value!.accountId],
    );

    async function reload(): Promise<ShortlistResponse | null> {
        try {
            shortlist.value = await shortlistApi.findCurrentShortlist();
        } catch (e) {
            shortlist.value = null;
        }

        return shortlist.value;
    }

    async function add(): Promise<string> {
        try {
            const shortlistTitle = route?.query.q ? (route.query.q as string) : shortlist.value?.title;
            if (!shortlistTitle) {
                throw new Error('Shortlist title is missing');
            }

            const {headers} = await shortlistApi.createOrUpdateShortlistRaw({
                createOrUpdateShortlistRequest: {
                    searchId: route?.query.searchid?.toString(),
                    title: shortlistTitle,
                    profileViewId: profile.value!.profileViewId,
                    freelancerAccountIds: accountIds.value,
                },
            });

            return headers.get('Location')!;
        } catch (error: any) {
            if (error.statusCode === 422) {
                const fullName = `${profile.value?.firstname} ${profile.value?.lastname}`;
                pushVJoySnackbar({
                    props: {
                        message: t('profile.shortlist.full.notification.error', [fullName]),
                        level: 'error',
                    },
                });
            } else {
                logger.error(error);
            }

            return '';
        }
    }

    async function remove(profileId?: string): Promise<void> {
        const id = profileId || findItemByAccountId.value?.profileId;
        if (id) {
            await shortlistApi.removeProfileFromShortlist({profileId: id});
            await reload();
        }
    }

    return {
        comesFromSearch,
        hasShortlistItems,
        profileInShortlist,
        shortlist,
        add,
        reload,
        remove,
    };
});
